import {
  ComponentClass,
  DuctedFuelToken,
  systemTypeCodeClasses,
  systemTypeCodeFuel,
  systemTypeCodeFurnaceKind,
  systemTypeCodePackagedToken
} from './kept-system-type-annotation';
import {
  BlowerComponentId,
  CoilComponentId,
  CompositionPicks,
  FurnaceFuel,
  OutdoorComponentId,
  PackagedCoolingId
} from './system-composition-types';

// Maps a row's compatibilityCodes onto CompositionPicks via the existing
// systemTypeCodeClasses / systemTypeCodeFuel grammar. Class membership is
// structural. Returns null when the row has no codes or no classes so the
// caller can fall back to parseSystemTypeName; the terminal `pt` token is
// deliberately unmapped (PTAC/PTHP are not composable in the picker).

const EMPTY_PICKS: CompositionPicks = {
  outdoor: null,
  blower: null,
  blowerFuel: null,
  coil: null,
  packagedCooling: null,
  packagedHeating: null,
  packagedFuel: null
};

type PackagedFromToken = {
  cooling: PackagedCoolingId;
  furnace: boolean;
};

const PACKAGED_FROM_TOKEN: Record<string, PackagedFromToken | undefined> = {
  pa: { cooling: 'packaged-ac', furnace: false },
  ph: { cooling: 'packaged-hp', furnace: false },
  pg: { cooling: 'packaged-ac', furnace: true },
  pd: { cooling: 'packaged-hp', furnace: true }
};

const OUTDOOR_FROM_CLASS: Partial<Record<ComponentClass, OutdoorComponentId>> =
  {
    condenser: 'ac',
    heatPump: 'hp',
    nextGenCondenser: 'next-gen-ac',
    nextGenHeatPump: 'next-gen-hp'
  };

const BLOWER_FROM_CLASS: Partial<Record<ComponentClass, BlowerComponentId>> = {
  airHandler: 'air-handler',
  nextGenAirHandler: 'next-gen-air-handler',
  modularBlower: 'modular-blower'
};

const COIL_FROM_CLASS: Partial<Record<ComponentClass, CoilComponentId>> = {
  evaporatorCoil: 'coil',
  nextGenEvaporatorCoil: 'next-gen-coil'
};

const FUEL_FROM_TOKEN: Record<DuctedFuelToken, FurnaceFuel> = {
  ng: 'ng',
  lp: 'lp',
  o: 'oil',
  e: 'electric'
};

const uniqueMapped = <T>(
  classes: Set<ComponentClass>,
  table: Partial<Record<ComponentClass, T>>
): T | null | undefined => {
  const mapped: T[] = [];
  for (const cls of classes) {
    const value = table[cls];
    if (value !== undefined && !mapped.includes(value)) {
      mapped.push(value);
    }
  }
  if (mapped.length === 0) return null;
  if (mapped.length > 1) return undefined;
  return mapped[0];
};

const picksFromClasses = (
  classes: Set<ComponentClass>,
  fuel: DuctedFuelToken,
  furnaceKind: BlowerComponentId | null,
  packagedToken: string | null
): CompositionPicks | null => {
  if (classes.size === 0) return null;
  if (classes.has('packaged')) {
    const packaged =
      packagedToken === null ? undefined : PACKAGED_FROM_TOKEN[packagedToken];
    if (packaged === undefined) return null;
    return {
      ...EMPTY_PICKS,
      packagedCooling: packaged.cooling,
      packagedHeating: packaged.furnace ? 'packaged-furnace' : null,
      packagedFuel: packaged.furnace ? FUEL_FROM_TOKEN[fuel] : null
    };
  }
  const outdoor = uniqueMapped(classes, OUTDOOR_FROM_CLASS);
  const coil = uniqueMapped(classes, COIL_FROM_CLASS);
  if (outdoor === undefined || coil === undefined) return null;
  let blower: BlowerComponentId | null = null;
  let blowerFuel: FurnaceFuel | null = null;
  if (classes.has('furnace')) {
    if (
      classes.has('airHandler') ||
      classes.has('nextGenAirHandler') ||
      classes.has('modularBlower')
    ) {
      return null;
    }
    blower = furnaceKind ?? 'furnace';
    blowerFuel = FUEL_FROM_TOKEN[fuel];
  } else {
    const mappedBlower = uniqueMapped(classes, BLOWER_FROM_CLASS);
    if (mappedBlower === undefined) return null;
    blower = mappedBlower;
  }
  return {
    ...EMPTY_PICKS,
    outdoor,
    blower,
    blowerFuel,
    coil
  };
};

export const picksFromCompatibilityCodes = (
  codes: string[]
): CompositionPicks | null => {
  if (codes.length === 0) return null;
  const classes = new Set<ComponentClass>();
  let furnaceKind: BlowerComponentId | null = null;
  let packagedToken: string | null = null;
  let fuel: DuctedFuelToken | null = null;
  for (const code of codes) {
    for (const cls of systemTypeCodeClasses(code)) {
      classes.add(cls);
    }
    const kind = systemTypeCodeFurnaceKind(code);
    if (kind) furnaceKind = kind;
    const token = systemTypeCodePackagedToken(code);
    if (token) packagedToken = token;
    if (fuel === null) fuel = systemTypeCodeFuel(code);
  }
  return picksFromClasses(classes, fuel ?? 'e', furnaceKind, packagedToken);
};
